import { Grid, Slide, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { MetaData } from '@sendbird/chat';
import { useApolloClient } from '@apollo/client';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { sendbirdSelectors, useSendbirdStateContext } from '@sendbird/uikit-react';
import { GroupChannelHandler } from '@sendbird/chat/groupChannel';
import '@sendbird/uikit-react/dist/index.css';
import '../assets/styles/sendbird.css';
import { UserMessageCreateParams } from '@sendbird/chat/message';
import { useGroupChannelListContext } from '@sendbird/uikit-react/GroupChannelList/context';
import { Chat_Message_Custom_Type } from '@kheops/utils';
import { useContractsByCompanyIdQuery } from '../queries/__generated__/contractsByCompanyId.generated';
import { ContractPropositionInfoDocument, useContractPropositionInfoLazyQuery } from '../queries/__generated__/contractPropositionInfo.generated';
import { PageWrapper } from '../common/components/PageWrapper';
import { ContractsAsBuyerDocument } from '../queries/__generated__/contractsAsBuyer.generated';
import { ContractsAsSupplierDocument } from '../queries/__generated__/contractsAsSupplier.generated';
import { currentContextAtom } from '../state';
import { currentChannelAtom, currentContractAtom, newChannelSlideOpenAtom, showChannelMembersAtom } from './state';
import ChannelList from './ChannelList';
import ChannelMemberList from './members/ChannelMemberList';
import useHandleBuyerAckNotification from './hooks/useHandleBuyerAckNotification';
import Channel from './Channel';
import ContactableCompanyList from './ContactableCompanyList';

export default function ChatListPage(): React.JSX.Element {
  const apolloClient = useApolloClient();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'));
  const [searchParams] = useSearchParams();

  const globalStore = useSendbirdStateContext();
  const sdkInstance = sendbirdSelectors.getSdk(globalStore);
  const defaultChannelUrl = searchParams.get('channelUrl');
  const { groupChannels, initialized } = useGroupChannelListContext();

  const { companyId } = useAtomValue(currentContextAtom);

  const [currentChannel, setCurrentChannel] = useAtom(currentChannelAtom);
  const setCurrentContract = useSetAtom(currentContractAtom);
  const showChannelMembers = useAtomValue(showChannelMembersAtom);
  const newChannelSlideOpen = useAtomValue(newChannelSlideOpenAtom);

  const { data: contractsData } = useContractsByCompanyIdQuery({ variables: { companyId } });
  const [contractPropositionInfoQuery, { data: contractPropositionInfoData }] = useContractPropositionInfoLazyQuery({ fetchPolicy: 'cache-and-network' });
  const [hasSetChannelUsingSearchParamOnce, setHasSetChannelUsingSearchParamOnce] = useState(false);
  const { handleBuyerAckNotification } = useHandleBuyerAckNotification();

  const handleBeforeMessageSent = useCallback((message: string): UserMessageCreateParams => {
    handleBuyerAckNotification(currentChannel);

    return { message };
  }, [handleBuyerAckNotification, currentChannel]);

  useEffect(() => {
    if (sdkInstance?.groupChannel) {
      const channelHandlerKey = 'offerReceptionHandler';
      const newGroupChannelHandler = new GroupChannelHandler({
        onMessageReceived: (channel, message) => {
          if (message.customType === Chat_Message_Custom_Type.OFFER || message.customType === Chat_Message_Custom_Type.ACCEPTED_OFFER) {
            apolloClient.refetchQueries({
              include: [ContractPropositionInfoDocument, ContractsAsSupplierDocument, ContractsAsBuyerDocument],
            });
          }
        },
      });

      sdkInstance.groupChannel.addGroupChannelHandler(channelHandlerKey, newGroupChannelHandler);

      return (): void => {
        sdkInstance.groupChannel.removeGroupChannelHandler(channelHandlerKey);
      };
    }
  }, [sdkInstance, apolloClient]);

  useEffect(() => {
    if (!initialized) {
      setHasSetChannelUsingSearchParamOnce(false);
    }
  }, [initialized]);

  useEffect((): void => {
    if (defaultChannelUrl && initialized && !hasSetChannelUsingSearchParamOnce) {
      setHasSetChannelUsingSearchParamOnce(true);
      setCurrentChannel(groupChannels.find(({ url }) => defaultChannelUrl === url)!);
    }
  }, [defaultChannelUrl, initialized]);

  useEffect((): void => {
    if (currentChannel) {
      const channelMetadata = currentChannel.cachedMetaData as MetaData;

      contractPropositionInfoQuery({
        variables: {
          buyingCompanyId: channelMetadata.buyingCompanyId,
          supplyingCompanyId: channelMetadata.supplyingCompanyId,
        },
      });
    }
  }, [currentChannel]);

  useEffect((): void => {
    const contract = contractPropositionInfoData?.contract?.[0];

    setCurrentContract(contract);
  }, [contractPropositionInfoData]);

  return (
    <PageWrapper
      sx={{
        px: 0,
        pt: {
          xs: 0,
          lg: 2,
        },
      }}
    >
      <Grid
        container
        sx={{
          height: {
            xs: 'calc(100vh - 44px)',
            lg: 'calc(100vh - 120px)',
            flexWrap: 'nowrap',
          },
          position: 'relative',
          '& .Kheops-ChatListPage-container': {
            backgroundColor: {
              xs: 'surface.main',
              lg: 'background.paper',
            },
            pb: 2,
          },
          '& .Kheops-ChatListPage-container, & .sendbird-conversation__messages-padding': {
            [theme.breakpoints.down('lg')]: {
              mt: 7,
              px: 2,
              pt: 0.5,
            },
          },
        }}
        columnSpacing={2}
      >
        {!isSmall && (
          <>
            <Grid size={4} sx={{ height: '100%', position: 'relative' }}>
              {(!!contractsData && newChannelSlideOpen) ? (
                <ContactableCompanyList contracts={contractsData!.contract} availableChannels={groupChannels} />
              ) : (
                <ChannelList />
              )}
            </Grid>
            <Grid
              size={showChannelMembers ? 4 : 8}
              sx={{
                transition: `${theme.transitions.create('width')}`,
                overflow: 'hidden',
              }}
            >
              <Channel url={currentChannel?.url || ''} handleBeforeMessageSent={handleBeforeMessageSent} />
            </Grid>
            <Grid
              size={showChannelMembers ? 4 : 0}
              sx={{
                transition: theme.transitions.create('width'),
                maxHeight: '100%',
                overflow: 'hidden',
              }}
            >
              <ChannelMemberList />
            </Grid>
          </>
        )}
        {isSmall && (
          <>
            <Slide direction="right" in={!currentChannel} mountOnEnter unmountOnExit>
              <Grid size={{ xs: 12 }} sx={{ position: 'absolute', height: '100%', width: '100%', zIndex: 10 }}>
                <ChannelList />
              </Grid>
            </Slide>
            {currentChannel && (
              <Grid size={{ xs: 12 }}>
                <Channel url={currentChannel?.url || ''} handleBeforeMessageSent={handleBeforeMessageSent} />
              </Grid>
            )}
            <Slide direction="left" in={showChannelMembers} mountOnEnter unmountOnExit>
              <Grid size={{ xs: 12 }} sx={{ position: 'absolute', height: '100%', width: '100%', zIndex: 10 }}>
                <ChannelMemberList />
              </Grid>
            </Slide>
            {!!contractsData && (
              <Slide direction="right" in={newChannelSlideOpen} mountOnEnter unmountOnExit>
                <Grid size={{ xs: 12 }} sx={{ position: 'absolute', height: '100%', width: '100%', zIndex: 10 }}>
                  <ContactableCompanyList contracts={contractsData.contract} availableChannels={groupChannels} />
                </Grid>
              </Slide>
            )}
          </>
        )}
      </Grid>
    </PageWrapper>
  );
}
